/**
 * DTOs para Administración de Agentes
 * Basado en AgentSqlController y AgentsAdminController del Backend
 */

import { BaseDto } from './base-dto';

// ==================== OVERVIEW ====================
export class AgentOverviewDto extends BaseDto {
    public agent_id: string = '';
    public agent_name: string = '';
    public installation_id: string = '';
    public environment_name: string = '';
    public is_online: boolean = false;
    public last_seen_at: Date | null = null;
    public version: string = '';
    public status: number = 0;
    public total_commands: number = 0;
    public total_connections: number = 0;
    public last_heartbeat: Date | null = null;

    constructor(data: any = {}) {
        super();
        if (!data) return;
        this.agent_id = data.agent_id || '';
        this.agent_name = data.agent_name || '';
        this.installation_id = data.installation_id || '';
        this.environment_name = data.environment_name || '';
        this.is_online = data.is_online !== undefined ? data.is_online : false;
        this.version = data.current_version || data.version || '';
        this.status = data.status !== undefined ? data.status : 0;
        this.total_commands = data.total_commands || 0;
        this.total_connections = data.total_connections || 0;

        if (data.last_seen_at_utc) {
            this.last_seen_at = new Date(data.last_seen_at_utc);
        }
        if (data.last_heartbeat) this.last_heartbeat = new Date(data.last_heartbeat);
    }
}

export class AgentsOverviewResponse {
    public agents: AgentOverviewDto[] = [];
    public agent_connections: ConnectionDto[] = [];
    public agent_heartbeats: HeartbeatDto[] = [];
    public agent_commands: CommandDto[] = [];
    public agent_command_results: CommandResultDto[] = [];
    public agent_audit_events: AuditEventDto[] = [];
    public agent_licenses: LicenseDto[] = [];
    public agent_releases: ReleaseDto[] = [];
    public agent_update_jobs: UpdateJobDto[] = [];

    constructor(data: any = {}) {
        if (data && data.agents) {
            this.agents = data.agents.map((a: any) => new AgentOverviewDto(a));
        }
        if (data && data.agent_connections) {
            this.agent_connections = data.agent_connections.map((c: any) => new ConnectionDto(c));
        }
        if (data && data.agent_heartbeats) {
            this.agent_heartbeats = data.agent_heartbeats.map((h: any) => new HeartbeatDto(h));
        }
        if (data && data.agent_commands) {
            this.agent_commands = data.agent_commands.map((cmd: any) => new CommandDto(cmd));
        }
        if (data && data.agent_command_results) {
            this.agent_command_results = data.agent_command_results.map((res: any) => new CommandResultDto(res));
        }
        if (data && data.agent_audit_events) {
            this.agent_audit_events = data.agent_audit_events.map((evt: any) => new AuditEventDto(evt));
        }
        if (data && data.agent_licenses) {
            this.agent_licenses = data.agent_licenses.map((lic: any) => new LicenseDto(lic));
        }
        if (data && data.agent_releases) {
            this.agent_releases = data.agent_releases.map((rel: any) => new ReleaseDto(rel));
        }
        if (data && data.agent_update_jobs) {
            this.agent_update_jobs = data.agent_update_jobs.map((job: any) => new UpdateJobDto(job));
        }
    }
}

// ==================== AGENT STATE ====================
export class AgentStateDto extends BaseDto {
    public agent_id: string = '';
    public is_online: boolean = false;
    public last_heartbeat: Date | null = null;
    public last_command_execution: Date | null = null;
    public active_connections: number = 0;
    public pending_tasks: number = 0;
    public memory_usage_mb: number = 0;
    public cpu_usage_percent: number = 0;
    public uptime_hours: number = 0;

    constructor(data: any = {}) {
        super();
        if (!data) return;
        this.agent_id = data.agent_id || '';
        this.is_online = data.is_online !== undefined ? data.is_online : false;
        this.last_heartbeat = data.last_heartbeat ? new Date(data.last_heartbeat) : null;
        this.last_command_execution = data.last_command_execution ? new Date(data.last_command_execution) : null;
        this.active_connections = data.active_connections || 0;
        this.pending_tasks = data.pending_tasks || 0;
        this.memory_usage_mb = data.memory_usage_mb || 0;
        this.cpu_usage_percent = data.cpu_usage_percent || 0;
        this.uptime_hours = data.uptime_hours || 0;
    }
}

// ==================== AGENTS ====================
export class AgentDto extends BaseDto {
    public agent_id: string = '';
    public agent_name: string = '';
    public installation_id: string = '';
    public tenant_id: string = '';
    public clinic_id: string = '';
    public environment_name: string = '';
    public machine_name: string = '';
    public hardware_id: string = '';
    public is_enabled: boolean = true;
    public is_deleted: boolean = false;
    public version: string = ''; // Maps to current_version
    public status: number = 0;
    public created_at: Date | null = null; // Maps to created_at_utc
    public updated_at: Date | null = null; // Maps to updated_at_utc
    public last_seen_at: Date | null = null; // Maps to last_seen_at_utc

    constructor(data: any = {}) {
        super();
        if (!data) return;

        // Manual mapping to handle backend snake_case to frontend camelCase
        this.agent_id = data.agent_id || '';
        this.agent_name = data.agent_name || data.machine_name || ''; // Use machine_name if agent_name not available
        this.installation_id = data.installation_id || '';
        this.tenant_id = data.tenant_id || '';
        this.clinic_id = data.clinic_id || '';
        this.environment_name = data.environment_name || '';
        this.machine_name = data.machine_name || '';
        this.hardware_id = data.hardware_id || '';
        this.is_enabled = data.is_enabled !== undefined ? data.is_enabled : true;
        this.is_deleted = data.is_deleted !== undefined ? data.is_deleted : false;
        this.version = data.current_version || data.version || '';
        this.status = data.status || 0;

        // Parse dates
        if (data.created_at_utc) this.created_at = new Date(data.created_at_utc);
        else if (data.created_at) this.created_at = new Date(data.created_at);

        if (data.updated_at_utc) this.updated_at = new Date(data.updated_at_utc);
        else if (data.updated_at) this.updated_at = new Date(data.updated_at);

        if (data.last_seen_at_utc) this.last_seen_at = new Date(data.last_seen_at_utc);
        else if (data.last_seen_at) this.last_seen_at = new Date(data.last_seen_at);
    }

    toJson(): any {
        return {
            agent_id: this.agent_id,
            agent_name: this.agent_name,
            installation_id: this.installation_id,
            tenant_id: this.tenant_id,
            clinic_id: this.clinic_id,
            environment_name: this.environment_name,
            machine_name: this.machine_name,
            hardware_id: this.hardware_id,
            is_enabled: this.is_enabled,
            is_deleted: this.is_deleted,
            current_version: this.version,
            status: this.status
        };
    }
}

// ==================== LICENSES ====================
export class LicenseDto extends BaseDto {
    public license_id: string = '';
    public agent_id: string = '';
    public license_key: string = '';
    public status: string = ''; // active, expired, revoked
    public expiration_date: Date | null = null;
    public max_installations: number = 0;
    public current_installations: number = 0;
    public created_date: Date | null = null;

    constructor(data: any = {}) {
        super();
        Object.assign(this, data);
        if (this.expiration_date) this.expiration_date = new Date(this.expiration_date);
        if (this.created_date) this.created_date = new Date(this.created_date);
    }
}

// ==================== CONNECTIONS ====================
export class ConnectionDto extends BaseDto {
    public connection_id: string = '';
    public agent_id: string = '';
    public installation_id: string = '';
    public transport_type: string = ''; // signalr, websocket, etc
    public connection_status: number = 0; // 0=disconnected, 1=connected, 2=error
    public connected_at_utc: Date | null = null;
    public last_seen_at_utc: Date | null = null;
    public disconnected_at_utc: Date | null = null;
    public disconnect_reason: string = '';
    public is_current: boolean = false;

    constructor(data: any = {}) {
        super();
        if (!data) return;
        this.connection_id = data.connection_id || '';
        this.agent_id = data.agent_id || '';
        this.installation_id = data.installation_id || '';
        this.transport_type = data.transport_type || '';
        this.connection_status = data.connection_status !== undefined ? data.connection_status : 0;
        this.disconnect_reason = data.disconnect_reason || '';
        this.is_current = data.is_current !== undefined ? data.is_current : false;
        
        if (data.connected_at_utc) this.connected_at_utc = new Date(data.connected_at_utc);
        if (data.last_seen_at_utc) this.last_seen_at_utc = new Date(data.last_seen_at_utc);
        if (data.disconnected_at_utc) this.disconnected_at_utc = new Date(data.disconnected_at_utc);
    }
}

// ==================== HEARTBEATS ====================
export class HeartbeatDto extends BaseDto {
    public agent_id: string = '';
    public installation_id: string = '';
    public overall_health_status: number = 0; // 1=healthy, 2=warning, 3=error
    public config_ok: boolean = false;
    public sql_ok: boolean = false;
    public sql_server: string = '';
    public sql_database: string = '';
    public sql_auth_mode: number = 0;
    public sql_elapsed_milliseconds: number = 0;
    public sql_server_version: string = '';
    public cpu_usage_percent: number = 0;
    public memory_used_mb: number = 0;
    public available_disk_mb: number = 0;
    public last_error: string = '';
    public last_error_at_utc: Date | null = null;
    public service_started_at_utc: Date | null = null;
    public snapshot_at_utc: Date | null = null;
    public received_at_utc: Date | null = null;

    constructor(data: any = {}) {
        super();
        if (!data) return;
        this.agent_id = data.agent_id || '';
        this.installation_id = data.installation_id || '';
        this.overall_health_status = data.overall_health_status !== undefined ? data.overall_health_status : 0;
        this.config_ok = data.config_ok !== undefined ? data.config_ok : false;
        this.sql_ok = data.sql_ok !== undefined ? data.sql_ok : false;
        this.sql_server = data.sql_server || '';
        this.sql_database = data.sql_database || '';
        this.sql_auth_mode = data.sql_auth_mode !== undefined ? data.sql_auth_mode : 0;
        this.sql_elapsed_milliseconds = data.sql_elapsed_milliseconds || 0;
        this.sql_server_version = data.sql_server_version || '';
        this.cpu_usage_percent = data.cpu_usage_percent || 0;
        this.memory_used_mb = data.memory_used_mb || 0;
        this.available_disk_mb = data.available_disk_mb || 0;
        this.last_error = data.last_error || '';

        if (data.last_error_at_utc) this.last_error_at_utc = new Date(data.last_error_at_utc);
        if (data.service_started_at_utc) this.service_started_at_utc = new Date(data.service_started_at_utc);
        if (data.snapshot_at_utc) this.snapshot_at_utc = new Date(data.snapshot_at_utc);
        if (data.received_at_utc) this.received_at_utc = new Date(data.received_at_utc);
    }
}

// ==================== COMMANDS ====================
export class CommandDto extends BaseDto {
    public command_id: string = '';
    public agent_id: string = '';
    public installation_id: string = '';
    public correlation_id: string = '';
    public command_name: string = '';
    public command_type: number = 0;
    public status: number = 0; // 0=pending, 4=completed, 5=failed (other intermediate statuses)
    public created_at: Date | null = null;
    public sent_at: Date | null = null;
    public accepted_at: Date | null = null;
    public started_at: Date | null = null;
    public completed_at: Date | null = null;
    public timeout_seconds: number = 90;
    public allow_retry: boolean = false;
    public max_retries: number = 0;
    public current_retry_count: number = 0;
    public payload_json: string = '';
    public payload_type: string = '';
    public created_by_user_id: string = '';

    constructor(data: any = {}) {
        super();
        if (!data) return;
        this.command_id = data.command_id || data.id || '';
        this.agent_id = data.agent_id || '';
        this.installation_id = data.installation_id || '';
        this.correlation_id = data.correlation_id || '';
        this.command_name = data.command_name || '';
        this.command_type = data.command_type !== undefined ? data.command_type : 0;
        this.status = data.status !== undefined ? data.status : 0;
        this.timeout_seconds = data.timeout_seconds || 90;
        this.allow_retry = data.allow_retry !== undefined ? data.allow_retry : false;
        this.max_retries = data.max_retries !== undefined ? data.max_retries : 0;
        this.current_retry_count = data.current_retry_count !== undefined ? data.current_retry_count : 0;
        this.payload_json = data.payload_json || '';
        this.payload_type = data.payload_type || '';
        this.created_by_user_id = data.created_by_user_id || '';

        if (data.created_at_utc) this.created_at = new Date(data.created_at_utc);
        else if (data.created_at) this.created_at = new Date(data.created_at);

        if (data.sent_at_utc) this.sent_at = new Date(data.sent_at_utc);
        else if (data.sent_at) this.sent_at = new Date(data.sent_at);

        if (data.accepted_at_utc) this.accepted_at = new Date(data.accepted_at_utc);
        else if (data.accepted_at) this.accepted_at = new Date(data.accepted_at);

        if (data.started_at_utc) this.started_at = new Date(data.started_at_utc);
        else if (data.started_at) this.started_at = new Date(data.started_at);

        if (data.finished_at_utc) this.completed_at = new Date(data.finished_at_utc);
        else if (data.completed_at) this.completed_at = new Date(data.completed_at);
    }
}

// ==================== COMMAND RESULTS ====================
export class CommandResultDto extends BaseDto {
    public command_result_id: string = '';
    public command_id: string = '';
    public agent_id: string = '';
    public success: boolean = false;
    public result_data: any = null;
    public result_payload_type: string = '';
    public result_payload_json: string = '';
    public final_status: number = 0;
    public duration_ms: number = 0;
    public is_retryable: boolean = false;
    public error_message: string = '';
    public error_code: string = '';
    public error_details: string = '';
    public started_at: Date | null = null;
    public finished_at: Date | null = null;
    public created_at: Date | null = null;

    constructor(data: any = {}) {
        super();
        if (!data) return;
        this.command_result_id = data.id || data.command_result_id || '';
        this.command_id = data.command_id || '';
        this.agent_id = data.agent_id || '';
        this.success = data.success !== undefined ? data.success : false;
        this.result_payload_type = data.result_payload_type || '';
        this.result_payload_json = data.result_payload_json || '';
        this.final_status = data.final_status !== undefined ? data.final_status : 0;
        this.duration_ms = data.duration_ms !== undefined ? data.duration_ms : 0;
        this.is_retryable = data.is_retryable !== undefined ? data.is_retryable : false;
        this.error_message = data.error_message || '';
        this.error_code = data.error_code || '';
        this.error_details = data.error_details || '';
        
        // Parse result_payload_json if available
        if (data.result_payload_json) {
            try {
                this.result_data = typeof data.result_payload_json === 'string' 
                    ? JSON.parse(data.result_payload_json) 
                    : data.result_payload_json;
            } catch (e) {
                this.result_data = data.result_payload_json;
            }
        }

        if (data.started_at_utc) this.started_at = new Date(data.started_at_utc);
        else if (data.started_at) this.started_at = new Date(data.started_at);

        if (data.finished_at_utc) this.finished_at = new Date(data.finished_at_utc);
        else if (data.finished_at) this.finished_at = new Date(data.finished_at);

        if (data.created_at_utc) this.created_at = new Date(data.created_at_utc);
        else if (data.created_at) this.created_at = new Date(data.created_at);
    }
}

// ==================== AUDIT EVENTS ====================
export class AuditEventDto extends BaseDto {
    public audit_id: string = '';
    public event_id: string = '';
    public agent_id: string = '';
    public event_type: string = ''; // heartbeat_received, agent_connected, agent_disconnected, command_executed, etc
    public source: string = '';
    public message: string = '';
    public details_json: string = '';
    public user_id: string = '';
    public created_at: Date | null = null;

    constructor(data: any = {}) {
        super();
        if (!data) return;
        this.audit_id = data.id || data.event_id || data.audit_id || '';
        this.event_id = data.event_id || data.id || '';
        this.agent_id = data.agent_id || '';
        this.event_type = data.event_type || '';
        this.source = data.source || '';
        this.message = data.message || '';
        this.details_json = data.details_json || '';
        this.user_id = data.user_id || '';
        
        if (data.created_at_utc) this.created_at = new Date(data.created_at_utc);
        else if (data.created_at) this.created_at = new Date(data.created_at);
    }
}

// ==================== RELEASES ====================
export class ReleaseDto extends BaseDto {
    public release_id: string = '';
    public version: string = '';
    public release_date: Date | null = null;
    public description: string = '';
    public download_url: string = '';
    public is_production: boolean = false;
    public is_latest: boolean = false;
    public checksum: string = '';

    constructor(data: any = {}) {
        super();
        Object.assign(this, data);
        if (this.release_date) this.release_date = new Date(this.release_date);
    }
}

// ==================== UPDATE JOBS ====================
export class UpdateJobDto extends BaseDto {
    public update_job_id: string = '';
    public agent_id: string = '';
    public from_version: string = '';
    public to_version: string = '';
    public status: string = ''; // pending, downloading, installing, completed, failed
    public progress_percent: number = 0;
    public created_at: Date | null = null;
    public started_at: Date | null = null;
    public completed_at: Date | null = null;
    public error_message: string = '';

    constructor(data: any = {}) {
        super();
        Object.assign(this, data);
        if (this.created_at) this.created_at = new Date(this.created_at);
        if (this.started_at) this.started_at = new Date(this.started_at);
        if (this.completed_at) this.completed_at = new Date(this.completed_at);
    }
}

// ==================== SQL QUERY REQUEST/RESPONSE ====================
export class SqlQueryRequestDto {
    public tenant_id: string = '';
    public agent_id: string = '';
    public installation_id: string = '';
    public sql: string = '';
    public max_rows: number = 200;

    constructor(data: any = {}) {
        Object.assign(this, data);
    }

    toJson(): any {
        return {
            tenant_id: this.tenant_id,
            agent_id: this.agent_id,
            installation_id: this.installation_id,
            sql: this.sql,
            max_rows: this.max_rows
        };
    }
}

export class SqlQueryResponseDto {
    public success: boolean = false;
    public command_id: string = '';
    public message: string = '';
    public rows: any[] = [];
    public columns: string[] = [];
    public rows_affected: number = 0;
    public execution_time_ms: number = 0;

    constructor(data: any = {}) {
        Object.assign(this, data);
    }
}
